import axios from "axios";
import { useCallback, useState } from "react";
import { environment } from "../../environment";
import { errorCatcher, useSnackbar } from "../../hooks";
import { useLogin } from "../login";

const gqlEndpoint = `${environment.baseUrl}/graphql`;

const graphqlRequest = async (authToken: string | null, query: string, variables?: any) => {
    const { data } = await axios.post(gqlEndpoint, { query, variables }, {
        headers: { "X-Wallmates-Auth": authToken }
    });
    if (data.errors) throw new Error(data.errors[0]?.message || "GraphQL error");
    return data.data;
};

// #region useDescriptionBannedWords
// Superadmin-only global list of words the AI description generator must never use.
export const useDescriptionBannedWords = () => {
    const [, setSnackbar] = useSnackbar();
    const [, authToken, loginFunctions] = useLogin();
    const [loading, setLoading] = useState(false);

    const run = useCallback(async <T,>(fn: () => Promise<T>): Promise<T | null> => {
        try {
            setLoading(true);
            return await fn();
        } catch (err: any) {
            errorCatcher(err, loginFunctions.logout);
            setSnackbar({ show: true, snackbarLevel: "error", text: err?.message || "Request failed" });
            return null;
        } finally {
            setLoading(false);
        }
    }, [loginFunctions.logout, setSnackbar]);

    // #region getDescriptionBannedWords
    const getDescriptionBannedWords = useCallback(() => run(async () => {
        const q = `query{ dcDescriptionBannedWords }`;
        const data = await graphqlRequest(authToken, q);
        return (data?.dcDescriptionBannedWords || []) as string[];
    }), [authToken, run]);

    // #region replaceDescriptionBannedWords
    const replaceDescriptionBannedWords = useCallback((words: string[]) => run(async () => {
        const q = `mutation($words:[String!]!){ replaceDCDescriptionBannedWords(words:$words) }`;
        const data = await graphqlRequest(authToken, q, { words });
        return (data?.replaceDCDescriptionBannedWords || []) as string[];
    }), [authToken, run]);

    return { loading, getDescriptionBannedWords, replaceDescriptionBannedWords };
};
